<template>
    <common-widget class="index-pending-connections">
        <common-widget-header :heading="$t('elements.home.referrals.heading')"></common-widget-header>

        <common-widget-content>
            <div v-if="totalCount > 0">
                <user-details
                    v-for="(connection, index) in pendingConnections"
                    :key="index"
                    :user="userData(connection)"
                    type="secondary"
                    :size="userDetailsSize"
                >
                    <a :class="getReinviteButtonClass(connection)" @click="reinviteConnection(connection)">
                        {{ $t('common.microcopy.reinvite') }}
                    </a>
                </user-details>
            </div>

            <common-content-spacer v-else class="index-pending-connections__invite-colleagues" height="small">
                <p class="index-pending-connections__connection-number">
                    {{ $t('pages.network.connections.message_box.no_pending_connections') }}
                </p>
                <common-content-spacer height="medium">
                    <invite-dropdown
                        :openInviteModal="openInviteModal"
                        :menuItems="menuItemsInvite"
                        :hasArrow="true"
                        :buttonLabel="$t('pages.network.connections.buttons.start_now')"
                    ></invite-dropdown>
                </common-content-spacer>
            </common-content-spacer>
        </common-widget-content>

        <div v-if="!!totalCount" class="index-pending-connections__show-more">
            <a v-if="totalCount > 3" @click="showAllClicked">
                {{ $t('pages.index.home.pending_connections.show_more', { n: totalCount - 3 }) }}
            </a>
            <invite-dropdown
                :openInviteModal="openInviteModal"
                :menuItems="menuItemsInvite"
                :hasArrow="true"
                :buttonLabel="$t('pages.network.connections.buttons.invite_new_member')"
            ></invite-dropdown>
        </div>

        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
        <send-join-invites-modal
            v-if="showInviteModal"
            :showImportContactsModal="true"
            :headerText="$t('elements.home.connections.modals.invite.physician.header_text')"
            :label="$t('elements.home.connections.modals.invite.physician.label')"
            :placeholder="$t('elements.home.connections.modals.invite.physician.placeholder')"
            :buttonLabel="$t('elements.home.connections.modals.invite.physician.button_label')"
            :practiceType="practiceType"
            :inviteBy="data.inviteBy"
            @join-invites-sent="invitesSent"
            @new-email-invitations-present="invitesSentAfterEmailCheck"
            @close="closeInviteModal"
        ></send-join-invites-modal>
    </common-widget>
</template>

<script>
    import dayjs from 'dayjs'
    import UsersAPI from 'api/UsersAPI'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import UserDetails from 'components/page-elements/referrals/details/UserDetailsCard'
    import InviteDropdown from 'components/page-elements/invites/InviteDropdown'
    import InviteByEnum from '@/enums/InviteByEnum'
    import SendJoinInvitesModal from 'components/page-elements/invites/SendJoinInvitesModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [ScreenSizeDetectionMixin],

        components: {
            UserDetails,
            SendJoinInvitesModal,
            InviteDropdown,
        },

        data() {
            return {
                data: {
                    inviteBy: InviteByEnum.INVITE_BY_EMAIL,
                },
                notificationMessage: '',
                pendingConnections: [],
                totalCount: 0,
                showInviteModal: false,
                showCheckInviteEmailsModal: false,
                showJoinInvitesErrorMessageCode: '',
                emailsCheck: {
                    invited: [],
                    registeredInvites: [],
                    registeredOther: [],
                    newInvitations: [],
                },
                isDropdownOpen: false,
                countryCode: '+1',
            }
        },

        computed: {
            userDetailsSize() {
                return this.isLaptop ? 'tiny' : 'small'
            },

            menuItemsInvite() {
                return [
                    {
                        name: this.$t('common.practice_type.physician'),
                        subItems: [
                            {
                                name: this.$t('elements.practice_member_filter.buttons.invite_by_email'),
                                payload: { inviteBy: 'invite_by_email', practiceType: PracticeTypeEnum.PHYSICIAN },
                            },
                            {
                                name: this.$t('elements.practice_member_filter.buttons.invite_by_phone'),
                                payload: { inviteBy: 'invite_by_phone', practiceType: PracticeTypeEnum.PHYSICIAN },
                            },
                        ],
                    },
                    {
                        name: this.$t('common.practice_type.ancillary'),
                        subItems: [
                            {
                                name: this.$t('elements.practice_member_filter.buttons.invite_by_email'),
                                payload: { inviteBy: 'invite_by_email', practiceType: PracticeTypeEnum.ANCILLARY },
                            },
                            {
                                name: this.$t('elements.practice_member_filter.buttons.invite_by_phone'),
                                payload: { inviteBy: 'invite_by_phone', practiceType: PracticeTypeEnum.ANCILLARY },
                            },
                        ],
                    },
                ]
            },
        },

        methods: {
            toggleDropdown() {
                this.isDropdownOpen = !this.isDropdownOpen
            },

            closeDropdown() {
                this.isDropdownOpen = false
            },
            loadPendingConnections() {
                const request = {
                    connectionTargetPracticeType: 0,
                    connectionDirection: 'All',
                    limit: 3,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                    totalCount: false,
                    filteredCount: true,
                    fields: null,
                    physicianId: this.$auth.getActiveMember().id,
                    withPractices: true,
                    registrationCompletionStatus: 'unfinished',
                    searchTerm: null,
                }
                return UsersAPI.getMyConnections(request).then((res) => {
                    this.pendingConnections = res
                    this.totalCount = res._headers['x-total-count'] ? res._headers['x-total-count'] : 0
                })
            },

            userData(connection) {
                const user = connection.user ? connection.user : connection.target
                if (connection.invite && connection.invite.phone) {
                    user.phone = connection.invite.phone
                }
                return user
            },

            invitationsSent() {
                this.notificationMessage = this.$t('modals.join_invites_member.invites_sent')
                this.$refs.commonSuccessNotificationChild.showNotification()
            },

            reinviteConnection(connection) {
                if (connection.user) {
                    UsersAPI.get(connection.target.id).then((res) => {
                        window.open(this.$t('native_emails.reinvite_existing_user', { recepientEmail: res.email }), '_blank')
                    })
                } else {
                    const toEmail = connection.target.name
                    const toPhone = connection.target.phone
                    const userId = this.$auth.getActiveMember().id
                    if (toPhone) {
                        UsersAPI.sendReminderForJoinInvite(
                            this.$auth.getActiveMember().id,
                            connection.invite.id,
                            'referral',
                            'create-practice',
                        ).then(() => {
                            this.invitationsSent()
                        })
                    } else {
                        UsersAPI.getJoinInviteLink(toEmail, userId).then((res) => {
                            window.open(
                                this.$t('native_emails.reinvite', {
                                    recepientEmail: toEmail,
                                    link: encodeURIComponent(res.link),
                                }),
                                '_blank',
                            )
                        })
                    }
                }
            },

            showAllClicked() {
                this.$router.push({ name: `network.connections`, query: { status: 'unfinished' } })
            },

            openInviteModal(payload) {
                this.practiceType = payload.practiceType
                this.data.inviteBy = payload.inviteBy
                this.showInviteModal = true
            },

            closeInviteModal() {
                this.showInviteModal = false
            },

            invitesSent() {
                this.closeInviteModal()
                this.loadPendingConnections()
                this.notificationMessage = this.$t('modals.join_invites_member.invites_sent')
                this.$refs.commonSuccessNotificationChild.showNotification()
            },

            checkInviteEmailsModal(event) {
                this.closeInviteModal()
                if (event.checkProperty && event.checkProperty.invited) {
                    event.checkProperty.invited.forEach((invited) => {
                        if (invited.phone) {
                            UsersAPI.sendReminderForJoinInvite(
                                this.$auth.getActiveMember().id,
                                invited.id,
                                'referral',
                                'create-practice',
                            ).then(() => {
                                this.invitationsSent()
                            })
                        }
                    })
                }
                if (this.data.inviteBy === InviteByEnum.INVITE_BY_EMAIL) {
                    this.showCheckInviteEmailsModal = true
                    this.emailsCheck = event.checkProperty
                }
            },

            invitesSentAfterEmailCheck(event) {
                UsersAPI.createJoinNetworkInvites(
                    this.$auth.getActiveMember().id,
                    event.emails,
                    'physician',
                    UserRolesEnum.PHYSICIAN.ID,
                ).then(() => {
                    this.loadPendingConnections()
                })
            },

            closeCheckInviteEmailsModal() {
                this.showCheckInviteEmailsModal = false
            },

            getReinviteButtonClass(connection) {
                const isWithinSevenDays = dayjs(connection.target.dateCreated).isAfter(dayjs().subtract(7, 'days'))
                return {
                    'index-pending-connections__reinvite': true,
                    [`index-pending-connections__reinvite--color-${isWithinSevenDays ? 'yellow' : 'blue'}`]: true,
                }
            },
        },

        watch: {
            $route() {
                if (this.$route.query?.openInviteModal?.length) {
                    const payload = {
                        practiceType: this.$route.query?.openInviteModal,
                        inviteBy: this.$route.query?.inviteBy,
                    }
                    this.openInviteModal(payload)
                    const query = Object.assign({}, this.$route.query)
                    delete query.openInviteModal
                    delete query.inviteBy
                    this.$router.replace({ query })
                }
            },
        },

        mounted() {
            this.loadPendingConnections()
        },
    }
</script>
